import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";

export const useApproachStore = defineStore("approach", () => {
  //data is loaded either from idb(pouch db for inspection module) or service(for management module)
  let pageData = ref({});

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getApproachObject = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_APPROACH;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Approach", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspLinkObject = (approach) => {
    return {
      brkey: approach.brkey,
      inspectionId: approach.inspectionId,
      inspectionComments: approach.inspectionComments,
      apprAlignDuration: approach.apprAlignDuration,
      apprRoadCondRateDuration: approach.apprRoadCondRateDuration,
      apprSlabCondRateDuration: approach.apprSlabCondRateDuration,
      railRatingDuration: approach.railRatingDuration,
      transRatingDuration: approach.transRatingDuration,
      apprPavementWidth: approach.APPR_PAVEMENT_WIDTH,
      apprAlign: approach.APPRALIGN,
      apprRoadCondRate: approach.APPR_ROAD_COND_RATE,
      apprSlabCondRate: approach.APPR_SLAB_COND_RATE,
      railRating: approach.RAILRATING,
      transRating: approach.TRANSRATIN,
      reliefJoint: approach.RELIEF_JOINT,
      structureSignings: approach.SIGNS,
      sfDescBridgeRailings: approach.BRIDGE_RAILING_DESC,
      sfDescTransitions: approach.TRANSITION_DESC,
      crashLevelBridgeRailings: approach.BridgeRailings,
      crashLevelTransitions: approach.Transitions,
    };
  };

  const convertToSaveInspModuleObject = (approachValue, structure) => {
    structure.inspectionConditionHist.apprAlignDuration = approachValue.apprAlignDuration;
    structure.inspectionConditionHist.apprRoadCondRateDuration = approachValue.apprRoadCondRateDuration;
    structure.inspectionConditionHist.apprSlabCondRateDuration = approachValue.apprSlabCondRateDuration;
    structure.inspectionConditionHist.railRatingDuration = approachValue.railRatingDuration;
    structure.inspectionConditionHist.transRatingDuration = approachValue.transRatingDuration;
    structure.inspectionConditionHist.aRailRatingDuration = approachValue.aRailRatingDuration;
    structure.inspectionConditionHist.aEndRatingDuration = approachValue.aEndRatingDuration;
    structure.userBridge.apprPavementWidth = approachValue.APPR_PAVEMENT_WIDTH;
    structure.inspection.apprAlign = approachValue.APPRALIGN;
    structure.userInspection.apprRoadCondRate = approachValue.APPR_ROAD_COND_RATE;
    structure.userInspection.apprSlabCondRate = approachValue.APPR_SLAB_COND_RATE;
    structure.inspection.railRating = approachValue.RAILRATING;
    structure.inspection.transRating = approachValue.TRANSRATIN;
    structure.inspection.aRailRating = approachValue.ARAILRATIN;
    structure.inspection.aEndRating = approachValue.AENDRATING;
    structure.userBridge.reliefJoint = approachValue.RELIEF_JOINT;
    //save only the undeleted signs
    structure.structureSignings = approachValue.SIGNS?.filter((s) => !s?.deleted);
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      apprAlignDuration: structure.inspectionConditionHist.apprAlignDuration,
      apprRoadCondRateDuration: structure?.inspectionConditionHist?.apprRoadCondRateDuration,
      apprSlabCondRateDuration: structure?.inspectionConditionHist?.apprSlabCondRateDuration,
      railRatingDuration: structure?.inspectionConditionHist?.railRatingDuration,
      transRatingDuration: structure?.inspectionConditionHist?.transRatingDuration,
      aRailRatingDuration: structure?.inspectionConditionHist?.aRailRatingDuration,
      aEndRatingDuration: structure?.inspectionConditionHist?.aEndRatingDuration,
      APPR_PAVEMENT_WIDTH: structure.userBridge.apprPavementWidth,
      APPRALIGN: structure.inspection.apprAlign,
      APPR_ROAD_COND_RATE: structure.userInspection.apprRoadCondRate,
      APPR_SLAB_COND_RATE: structure.userInspection.apprSlabCondRate,
      RAILRATING: structure.inspection.railRating,
      TRANSRATIN: structure.inspection.transRating,
      RELIEF_JOINT: structure.userBridge?.reliefJoint,
      SIGNS: structure.structureSignings,
      CRASH_LEVEL_BRIDGE_RAILINGS: structure.structureBridge.bridgeRailings,
      CRASH_LEVEL_TRANSITIONS: structure.structureBridge.transitions,
    };
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    //clear idb object
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_APPROACH.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((resp) => {
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      apprAlignDuration: response.apprAlignDuration,
      apprRoadCondRateDuration: response.apprRoadCondRateDuration,
      apprSlabCondRateDuration: response.apprSlabCondRateDuration,
      railRatingDuration: response.railRatingDuration,
      transRatingDuration: response.transRatingDuration,
      APPR_PAVEMENT_WIDTH: response.apprPavementWidth,
      APPRALIGN: response.apprAlign,
      APPR_ROAD_COND_RATE: response.apprRoadCondRate,
      APPR_SLAB_COND_RATE: response.apprSlabCondRate,
      RAILRATING: response.railRating,
      TRANSRATIN: response.transRating,
      RELIEF_JOINT: response.reliefJoint,
      SIGNS: getUnDeletedSigns(response.structureSignings),
      BRIDGE_RAILING_DESC: response.sfDescBridgeRailings,
      TRANSITION_DESC: response.sfDescTransitions,
      inspectionComments: response.inspectionComments,
      CRASH_LEVEL_BRIDGE_RAILINGS: response.crashLevelBridgeRailings,
      CRASH_LEVEL_TRANSITIONS: response.crashLevelTransitions,
    };
  };

  const getUnDeletedSigns = (signs) => {
    return signs?.filter((s) => !s.deleted);
  };

  return {
    getApproachObject,
    saveData,
    loadDataObject,
  };
});
